import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  extend,
  params,
  arrayOf,
  number,
  shape,
  string,
  useDataModel
} from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { RetailMediaCarousel } from '../RetailMediaCarousel';
import { HYDRATION_STANDARD_SCROLL_BUFFER, CAROUSEL_HYDRATION_STANDARD_SCROLL_BUFFER } from '../../constants';
import { dataModel as retailMediaCarouselDataModel } from '../RetailMediaCarousel/RetailMediaCarousel.dataModel';

const ZoneCard = ({ children }) => {
  return <div className="zone-card">{children}</div>;
};
ZoneCard.propTypes = {
  children: PropTypes.node.isRequired
};

export const RetailMediaCarouselFusionComposable = (props) => {
  const thdCustomer = useThdCustomer();
  const {
    hideSponsoredCarousel,
    hydratorClassName,
    hydratorScrollBuffer,
    itemId
  } = props;
  const { customContext } = useContext(ExperienceContext);
  const {
    variables = {},
    isSearch,
    isPLP,
    isBrowse,
    hybridLoading: isHybridLoading,
    isBrandPage,
    pageType = ''
  } = customContext || {};

  const defaultOptions = {
    variables,
    ssr: true,
    skip: (!variables.navParam && !variables.keyword),
  };

  const { data } = useDataModel('searchModel', defaultOptions);
  const { products = [], metadata, taxonomy } = data?.searchModel || {};
  const { contentType } = metadata || {};
  const isCategory = contentType === 'categorypage';

  // PLP rendering logic
  if (isHybridLoading && isBrandPage && isPLP && !data) {
    // If hideSponsoredCarousel is not passed as prop (null), do not render
    if (hideSponsoredCarousel === null) {
      return null;
    }
    if (hideSponsoredCarousel) {
      return null;
    }
  }

  // PEP rendering logic
  if (hideSponsoredCarousel && !isPLP && !data) {
    return null;
  }

  const sharedProps = {
    errorBoundary: true,
    name: 'sponsored-carousel-nucleus',
    showSponsoredCarousel: true,
    breadCrumbs: taxonomy?.breadCrumbs,
    categorySourceId: metadata?.categoryID,
  };

  const plpProps = {
    ...sharedProps,
    nValue: variables?.navParam,
    browserId: thdCustomer?.browserId,
    userId: thdCustomer?.svocID || thdCustomer?.userID || thdCustomer?.guestUserID,
    pageContext: {
      label: 'browse-search',
      data: {
        itemIds: products?.map(({ itemId: id }) => id) || []
      },
      keyword: variables?.keyword || '',
      isCategory,
      isBrowse,
      isSearch
    },
    dynamic: {
      pageType
    },
    hydrator: {
      className: hydratorClassName || 'grid zone-card',
      delay: 1500,
      scrollBuffer: hydratorScrollBuffer ?? CAROUSEL_HYDRATION_STANDARD_SCROLL_BUFFER
    }
  };

  const pepProps = {
    ...sharedProps,
    pageContext: {
      schema: 'pip_sponsored',
      data: { itemId },
      isPip: true
    },
    dynamic: {
      pageType: 'pip'
    },
    hydrator: {
      className: 'grid',
      tag: ZoneCard,
      delay: 1500,
      scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
    }
  };

  const componentProps = isPLP ? plpProps : pepProps;

  return (
    <RetailMediaCarousel
      {...componentProps}
    />
  );
};

RetailMediaCarouselFusionComposable.displayName = 'RetailMediaCarouselFusionComposable';

RetailMediaCarouselFusionComposable.propTypes = {
  itemId: PropTypes.string,
  hideSponsoredCarousel: PropTypes.oneOf([true, false, null]),
  hydratorClassName: PropTypes.string,
  hydratorScrollBuffer: PropTypes.number,
};

RetailMediaCarouselFusionComposable.defaultProps = {
  itemId: '',
  hideSponsoredCarousel: null,
  hydratorClassName: '',
  hydratorScrollBuffer: CAROUSEL_HYDRATION_STANDARD_SCROLL_BUFFER,
};

RetailMediaCarouselFusionComposable.dataModel = extend({
  searchModel: params({
    keyword: string(),
    navParam: string(),
  }).shape({
    products: arrayOf(shape({
      itemId: string(),
    })),
    metadata: shape({
      contentType: string(),
      categoryID: string(),
    }),
    taxonomy: shape({
      breadCrumbs: arrayOf(shape({
        browseUrl: string(),
        label: string(),
      }))
    }),
  }),
}, retailMediaCarouselDataModel);